import { useAuth0 } from '@auth0/auth0-react';
import React, { useEffect, useState } from 'react';
import styled from 'styled-components';
import twinlabsAiStackedLogo from '@repo/assets/logos/twinlabs-ai-stacked-logo.svg';

// Data

// Utils
import { clearOrganizationData, legalDocumentLinks } from '@repo/front-end-utils';

// Types
import { Legal } from '@repo/frontend-types';

// Context
import { useUserStore } from '@repo/front-end-state';

// Components
import { useLocation, useNavigate } from 'react-router-dom';
import { TLSpinnerPage, PrimaryButton } from '@repo/ui';

import Config from '../../../common/Config';
import { acceptTerms, RequiredLegalDocument } from '@repo/front-end-services';
interface Props {
  className?: string;
}

const LegalPromptPage: React.FC<Props> = ({ className }) => {
  const { isAuthenticated, isLoading, getAccessTokenSilently, logout } = useAuth0();
  const [accessToken, setAccessToken] = useState<string | null>(null);
  const navigate = useNavigate();
  const location = useLocation();
  const { setAuthMetadata } = useUserStore();

  useEffect(() => {
    const getAccessToken = async () => {
      setAccessToken(await getAccessTokenSilently());
    };
    getAccessToken();
  }, [isAuthenticated, getAccessTokenSilently]);

  const [declined, setDeclined] = useState<boolean>();
  const { required, redirectPath } = (location.state || {}) as {
    required?: RequiredLegalDocument[];
    organisationBid?: string | null;
    redirectPath?: string;
  };
  const requiredDocuments: RequiredLegalDocument[] = required ?? [];

  // A direct refresh / new-tab of /account/legal loses react-router's location.state, leaving no
  // required-documents payload — there is nothing to render or post. Bounce back through the gated
  // route so the protected gate recomputes status and (if the user still owes an acceptance) redirects
  // here again with a fresh payload; otherwise the user simply proceeds into the app.
  useEffect(() => {
    if (accessToken && requiredDocuments.length === 0) {
      navigate(redirectPath || '/twin', { replace: true });
    }
  }, [accessToken, requiredDocuments.length, redirectPath, navigate]);

  // Legal document links. twin-viewer is a separate deployment from apps/twin (which hosts the
  // public /legal pages), so links are absolute. Dark-launch: when REACT_APP_TWIN_APP_BASE_URL
  // is unset the shared helper returns null and we fall back to the marketing Webflow URLs (the
  // accept page shows the CURRENT wording either way); once it is set the links point at the
  // platform-owned pages. The derivation itself is shared with the re-consent-notice bell items
  // (legalDocumentLinks) — this page keeps its OWN Webflow fallback (a notice, by contrast, has
  // no Webflow equivalent and renders link-less). Swapping href only — gate logic is untouched.
  const legalLinks = legalDocumentLinks(Config.twinAppBaseUrl) ?? {
    saasStandardTerms: 'https://www.twinlabs.ai/twinlabs-ai-saas-standard-terms-and-conditions',
    dataProcessing: 'https://www.twinlabs.ai/data-processing',
    acceptableUse: 'https://www.twinlabs.ai/acceptable-use',
  };

  const acceptLegal = async () => {
    if (accessToken && (await acceptTerms(Config.middlewareApi, accessToken, requiredDocuments))) {
      // Optimistically mirror the accepted versions onto authMetadata.legal so navigation feels
      // instant; the protected gate re-verifies server-side regardless.
      const now = new Date().toISOString();
      const updatedLegal: Legal = {};
      for (const doc of requiredDocuments) {
        updatedLegal[doc.document] = { version: doc.version, acceptedAt: now };
      }
      setAuthMetadata({ legal: updatedLegal });

      // Navigate to the redirect path
      navigate(redirectPath || '/twin', { replace: true });
    } else {
      navigate('/something-went-wrong', { replace: true });
    }
  };

  const handleDeclineAndLogout = () => {
    // Clear organization data before logout
    clearOrganizationData();
    // Logout the user and redirect to login page
    logout({
      logoutParams: { returnTo: window.location.origin },
    });
  };

  // Spinner while loading, or while the empty-payload bounce above is in flight (avoids flashing an
  // empty "please accept our ." prompt).
  if (isLoading || !accessToken || requiredDocuments.length === 0) {
    return <TLSpinnerPage loadingMessages={[]} />;
  }

  return (
    <>
      {accessToken && (
        <div className={className}>
          <div className="logo">
            <img src={twinlabsAiStackedLogo} alt="Twinlabs.ai Logo" />
          </div>
          <h1>Terms And Conditions</h1>
          {!declined ? (
            <>
              <p>
                To proceed, please accept our{' '}
                {requiredDocuments.map((doc, index) => (
                  <React.Fragment key={doc.document}>
                    <a href={legalLinks[doc.document]} target="_blank" rel="noreferrer">
                      {doc.title}
                    </a>
                    {index < requiredDocuments.length - 2
                      ? ', '
                      : index === requiredDocuments.length - 2
                        ? ' and '
                        : '.'}
                  </React.Fragment>
                ))}
              </p>
              <p>
                By clicking Accept you are confirming you have read and accepted these terms and
                conditions.
              </p>
              <PrimaryButton
                onClick={() => {
                  acceptLegal();
                }}
              >
                Accept
              </PrimaryButton>
              <button
                className="secondary-button"
                onClick={() => {
                  setDeclined(true);
                }}
              >
                Decline
              </button>
            </>
          ) : (
            <>
              <p>
                Please contact <a href="mailto:support@twinlabs.ai">support@twinlabs.ai</a> to
                discuss. We are happy to answer any questions.
              </p>
              <PrimaryButton
                onClick={() => {
                  handleDeclineAndLogout();
                }}
              >
                Return To Login
              </PrimaryButton>
            </>
          )}
        </div>
      )}
    </>
  );
};

export default styled(LegalPromptPage)`
  .logo {
    display: flex;
    justify-content: center;
    margin-bottom: 1.5rem;
  }

  h1 {
    font-size: 1.45rem;
    text-align: center;
    margin-bottom: 1rem;
  }

  p {
    color: #424242;
    font-size: 0.875rem;
    text-align: center;
    margin-bottom: 1rem;
  }

  a:link,
  a:visited {
    color: #00bbcc;
    text-decoration: underline;
  }

  button {
    width: 100%;
    font-size: 0.875rem !important;
    height: 58px !important;
    margin-top: 1rem;
  }

  .secondary-button {
    border: 2px solid #d9d9d9;
    border-radius: 8px;
  }
`;
